import { ChangeDetectionStrategy, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { SelectedPanelEnum } from '@app/core/enums/selected-panel.enum';
import { ScrollService } from '@app/core/scroll/scroll.service';
import { IntersectionObserverService } from '@app/core/services/intersection-observer.service';
import { productIconConfig } from '@app/my-zone/config/product-icon.config';
import { StartDateTypeEnum } from '@app/my-zone/enums';
import { InsurancesQueryParamsInterface } from '@app/my-zone/interfaces/insurances-query-params.interface';
import { ProductNameEnum } from '@app/store/my-zone/product/product-name.enum';
import { PolicyValidityStatusCodeEnum, PurchasedProductInterface, RelationInterface } from 'outshared-lib';
import { ValidityStatusCodeEnum } from 'outshared-lib';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

@Component({
    selector: 'ins-insurance-list',
    templateUrl: './insurance-list.component.html',
    styleUrls: ['./insurance-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class InsuranceListComponent implements OnInit {
    @Input() public purchasedProduct!: PurchasedProductInterface;
    @Input() public relation!: RelationInterface;

    public selectedPanel: SelectedPanelEnum;
    public selectedPanelEnum: typeof SelectedPanelEnum;
    public validityStatusCodeEnum: typeof PolicyValidityStatusCodeEnum;
    public productIconName: string;
    public isTest: boolean;
    public startDateTypeEnum: typeof StartDateTypeEnum;
    public insuranceListId: string;
    public productNameEnum: typeof ProductNameEnum = ProductNameEnum;
    public validityStatusCode: typeof ValidityStatusCodeEnum = ValidityStatusCodeEnum;
    public DOG_BREED_UNKNOWN = 'DIER0999';
    public CAT_BREED_UNKNOWN = 'DIER0998';
    public isScreenSmall$: Observable<boolean>;
    public isPanelOpen: boolean = false;

    constructor(
        private route: ActivatedRoute,
        private scrollService: ScrollService,
        private intersectionObserverService: IntersectionObserverService
    ) {
        this.selectedPanel = SelectedPanelEnum.Close;
        this.selectedPanelEnum = SelectedPanelEnum;
        this.validityStatusCodeEnum = PolicyValidityStatusCodeEnum;
        this.isTest = false;
        this.startDateTypeEnum = StartDateTypeEnum;
        this.insuranceListId = 'insurance-list-';
        this.isScreenSmall$ = this.intersectionObserverService.isScreenSmall$;
    }

    public ngOnInit(): void {
        // Get product icon name
        this.getProductIconName();
        this.validateQueryParams();
    }

    public get isCurrent(): boolean {
        return [
            PolicyValidityStatusCodeEnum.Active,
            PolicyValidityStatusCodeEnum.ActiveEnded,
            PolicyValidityStatusCodeEnum.Disbarred,
            PolicyValidityStatusCodeEnum.Ended,
            PolicyValidityStatusCodeEnum.ActiveChange,
        ].includes(this.purchasedProduct.product.validityStatusCode);
    }

    // Future equals 'OSTP', 'TOEK'
    public get isFuture(): boolean {
        return [PolicyValidityStatusCodeEnum.Transfer, PolicyValidityStatusCodeEnum.Future].includes(
            this.purchasedProduct.product.validityStatusCode
        );
    }

    private getProductIconName(): void {
        const productIconName = productIconConfig[this.purchasedProduct?.productId];
        this.productIconName = productIconName ?? '';
    }

    private validateQueryParams(): void {
        this.route.queryParams
            .pipe(
                filter((params: InsurancesQueryParamsInterface) =>
                    Boolean(params.productSequenceNumber && params.panel)
                )
            )
            .subscribe({
                next: (params) => this.handleQueryParams(params),
            });
    }

    private handleQueryParams(params: InsurancesQueryParamsInterface): void {
        const productSequenceNumber: number = Number(params.productSequenceNumber);

        if (productSequenceNumber !== this.purchasedProduct.productSequenceNumber) {
            return;
        }

        this.openPanel(params);
        this.setSelectedPanel(params);
        this.scrollToPanel(params);
    }

    private openPanel(params: InsurancesQueryParamsInterface): void {
        if (params.panel === SelectedPanelEnum.Close) {
            this.isPanelOpen = false;
        } else {
            this.isPanelOpen = true;
        }
    }

    private setSelectedPanel(params: InsurancesQueryParamsInterface): void {
        this.selectedPanel = params.panel;
    }

    private scrollToPanel(params: InsurancesQueryParamsInterface): void {
        const productSequenceNumber: number = Number(params.productSequenceNumber);
        const elementid = `#${this.insuranceListId}${this.purchasedProduct.productSequenceNumber}`;
        const canScroll =
            Boolean(params.scroll === 'true') &&
            Boolean(productSequenceNumber === this.purchasedProduct.productSequenceNumber);

        if (!canScroll) {
            return;
        }

        this.scrollService.scrollElementOffset(elementid, 0, 0);
    }
}
